import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';


const statusConfig = {
  draft: { label: 'Draft', color: '#94a3b8', bg: 'rgba(148,163,184,0.12)', icon: 'FileText', desc: 'Not yet submitted' },
  pending_review: { label: 'Under Review', color: '#fbbf24', bg: 'rgba(251,191,36,0.12)', icon: 'Clock', desc: 'Awaiting admin approval (24–48 hrs)' },
  active: { label: 'Active', color: '#34d399', bg: 'rgba(52,211,153,0.12)', icon: 'CheckCircle', desc: 'Live and accepting bookings' },
  suspended: { label: 'Suspended', color: '#f87171', bg: 'rgba(248,113,113,0.12)', icon: 'AlertCircle', desc: 'Contact support for details' },
};

// Signed URL thumbnail for vehicle media
const VehicleThumbnail = ({ media }) => {
  const [imgUrl, setImgUrl] = useState(null);
  const primaryMedia = media?.find(m => m?.is_primary) || media?.[0];

  useEffect(() => {
    if (primaryMedia?.storage_path) {
      supabase?.storage
        ?.from('vehicle-media')
        ?.createSignedUrl(primaryMedia?.storage_path, 3600)
        ?.then(({ data }) => { if (data?.signedUrl) setImgUrl(data?.signedUrl); });
    }
  }, [primaryMedia?.storage_path]);

  if (imgUrl) {
    return (
      <div className="w-20 h-16 rounded-xl overflow-hidden flex-shrink-0">
        <img src={imgUrl} alt="Vehicle thumbnail" className="w-full h-full object-cover" />
      </div>
    );
  }
  return (
    <div className="w-20 h-16 rounded-xl bg-white/10 flex items-center justify-center text-3xl flex-shrink-0">🚗</div>
  );
};

// Completeness bar component
const CompletenessBar = ({ vehicle, mediaCount, pricing }) => {
  const score = Math.min(100, Math.round(
    ((vehicle?.title ? 15 : 0) +
      (vehicle?.make ? 10 : 0) +
      (vehicle?.model ? 10 : 0) +
      (vehicle?.location_address ? 15 : 0) +
      (pricing ? 20 : 0) +
      (mediaCount >= 6 ? 30 : mediaCount * 5))
  ));
  const color = score >= 80 ? '#34d399' : score >= 50 ? '#fbbf24' : '#f87171';
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-white/40 text-xs">Listing completeness</span>
        <span className="text-xs font-bold" style={{ color }}>{score}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${score}%`, background: color }} />
      </div>
    </div>
  );
};

// Retry upload modal
const RetryUploadModal = ({ vehicle, onClose, onSuccess }) => {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const fileInputRef = useRef(null);
  const currentCount = vehicle?.vehicle_media?.length || 0;
  const needed = Math.max(0, 6 - currentCount);

  const handleFileChange = (e) => {
    const files = Array.from(e?.target?.files || []);
    setPhotos(prev => [...prev, ...files]?.slice(0, 12 - currentCount));
  };

  const handleUpload = async () => {
    if (photos?.length === 0) return;
    setUploading(true);
    const failures = [];
    const successes = [];

    for (let i = 0; i < photos?.length; i++) {
      const photo = photos?.[i];
      const path = `vehicles/${vehicle?.id}/${Date.now()}_retry_${i}_photo.jpg`;
      const { data: uploadData, error: uploadError } = await supabase?.storage?.from('vehicle-media')?.upload(path, photo);

      if (uploadError) {
        failures?.push(`Photo ${i + 1} (${photo?.name || 'unnamed'}): ${uploadError?.message}`);
      } else {
        const sortOrder = currentCount + i;
        const { error: mediaError } = await supabase?.from('vehicle_media')?.insert({
          vehicle_id: vehicle?.id,
          storage_path: uploadData?.path,
          sort_order: sortOrder,
          is_primary: currentCount === 0 && i === 0,
        });
        if (mediaError) {
          failures?.push(`Photo ${i + 1} uploaded but record failed: ${mediaError?.message}`);
        } else {
          successes?.push(photo?.name || `Photo ${i + 1}`);
        }
      }
    }

    setUploadResults({ failures, successes, newTotal: currentCount + successes?.length });
    setUploading(false);
    if (successes?.length > 0) onSuccess?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-md rounded-2xl p-6 space-y-5" style={{ background: '#0f1f38', border: '1px solid rgba(255,255,255,0.12)' }}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-bold text-lg">Retry Photo Upload</h3>
            <p className="text-white/40 text-xs mt-0.5">{vehicle?.title}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
            <Icon name="X" size={18} className="text-white/60" />
          </button>
        </div>

        {/* Current status */}
        <div className="rounded-xl p-4 flex items-center gap-4" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="text-center">
            <div className={`text-2xl font-black ${currentCount >= 6 ? 'text-[#34d399]' : 'text-[#fbbf24]'}`}>{currentCount}</div>
            <div className="text-white/40 text-xs">Current</div>
          </div>
          <div className="flex-1 h-px bg-white/10" />
          <div className="text-center">
            <div className="text-2xl font-black text-white/30">6</div>
            <div className="text-white/40 text-xs">Minimum</div>
          </div>
          {needed > 0 && (
            <>
              <div className="flex-1 h-px bg-white/10" />
              <div className="text-center">
                <div className="text-2xl font-black text-[#f87171]">{needed}</div>
                <div className="text-white/40 text-xs">Still needed</div>
              </div>
            </>
          )}
        </div>

        {!uploadResults ? (
          <>
            {/* File picker */}
            <div
              onClick={() => fileInputRef?.current?.click()}
              className="rounded-xl p-6 flex flex-col items-center gap-3 cursor-pointer hover:bg-white/5 transition-colors"
              style={{ border: '2px dashed rgba(255,215,0,0.3)' }}
            >
              <Icon name="Upload" size={28} className="text-[#ffd700]" />
              <div className="text-center">
                <p className="text-white font-semibold text-sm">Click to select photos</p>
                <p className="text-white/40 text-xs mt-1">JPG, PNG, HEIC — up to {12 - currentCount} more photos</p>
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} />
            </div>

            {photos?.length > 0 && (
              <div>
                <p className="text-white/60 text-xs mb-2 font-semibold">{photos?.length} photo{photos?.length !== 1 ? 's' : ''} selected:</p>
                <div className="grid grid-cols-4 gap-2">
                  {photos?.map((p, i) => (
                    <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-white/10">
                      <img src={URL.createObjectURL(p)} alt={`Selected photo ${i + 1}`} className="w-full h-full object-cover" />
                      <button
                        onClick={(e) => { e?.stopPropagation(); setPhotos(prev => prev?.filter((_, idx) => idx !== i)); }}
                        className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center"
                      >
                        <Icon name="X" size={8} className="text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 py-3 rounded-xl text-white/60 text-sm font-semibold hover:bg-white/5 transition-colors" style={{ border: '1px solid rgba(255,255,255,0.12)' }}>
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={photos?.length === 0 || uploading}
                className="flex-1 py-3 rounded-xl text-sm font-bold transition-all disabled:opacity-40"
                style={{ background: photos?.length > 0 ? '#ffd700' : 'rgba(255,215,0,0.2)', color: photos?.length > 0 ? '#0a1628' : '#ffd700' }}
              >
                {uploading ? 'Uploading...' : `Upload ${photos?.length > 0 ? photos?.length + ' Photo' + (photos?.length !== 1 ? 's' : '') : 'Photos'}`}
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-4">
            {uploadResults?.successes?.length > 0 && (
              <div className="rounded-xl p-4" style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.25)' }}>
                <p className="text-[#34d399] font-semibold text-sm mb-1">✅ {uploadResults?.successes?.length} photo{uploadResults?.successes?.length !== 1 ? 's' : ''} uploaded successfully</p>
                <p className="text-white/50 text-xs">Total photos now: {uploadResults?.newTotal}</p>
              </div>
            )}
            {uploadResults?.failures?.length > 0 && (
              <div className="rounded-xl p-4" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)' }}>
                <p className="text-red-400 font-semibold text-sm mb-1">⚠️ {uploadResults?.failures?.length} photo{uploadResults?.failures?.length !== 1 ? 's' : ''} failed</p>
                <ul className="text-red-300/70 text-xs space-y-0.5">
                  {uploadResults?.failures?.map((f, i) => <li key={i}>• {f}</li>)}
                </ul>
              </div>
            )}
            <button
              onClick={onClose}
              className="w-full py-3 rounded-xl text-sm font-bold"
              style={{ background: '#ffd700', color: '#0a1628' }}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const MyVehiclesScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retryVehicle, setRetryVehicle] = useState(null);
  const [toastMsg, setToastMsg] = useState(null);

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [vehiclesRes, bookingsRes] = await Promise.all([
        supabase?.from('vehicles')?.select(`*, vehicle_pricing(*), vehicle_media(*)`)?.eq('owner_id', user?.id)?.order('created_at', { ascending: false }),
        supabase?.from('rental_bookings')?.select('vehicle_id, booking_status, price_breakdown')?.eq('owner_id', user?.id),
      ]);
      setVehicles(vehiclesRes?.data || []);
      setBookings(bookingsRes?.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const getVehicleStats = (vehicleId) => {
    const vBookings = bookings?.filter(b => b?.vehicle_id === vehicleId);
    const completed = vBookings?.filter(b => b?.booking_status === 'completed');
    const earnings = completed?.reduce((sum, b) => sum + (b?.price_breakdown?.total || 0) * 0.9, 0);
    return { totalBookings: vBookings?.length, earnings };
  };

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3500);
  };

  const handleRetrySuccess = () => {
    fetchData();
    showToast('Photos uploaded successfully!');
  };

  const pendingCount = vehicles?.filter(v => v?.status === 'pending_review')?.length;

  return (
    <div className="min-h-screen" style={{ background: '#0a1628', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');`}</style>

      {retryVehicle && (
        <RetryUploadModal
          vehicle={retryVehicle}
          onClose={() => setRetryVehicle(null)}
          onSuccess={handleRetrySuccess}
        />
      )}

      {/* Toast */}
      {toastMsg && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-2xl text-sm font-semibold shadow-xl" style={{ background: '#34d399', color: '#0a1628' }}>
          ✅ {toastMsg}
        </div>
      )}

      {/* Header */}
      <div className="sticky top-0 z-40 border-b border-white/10" style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
              <Icon name="ArrowLeft" size={20} className="text-white" />
            </button>
            <div>
              <h1 className="text-white font-bold text-lg">My Vehicles</h1>
              {pendingCount > 0 && (
                <p className="text-[#fbbf24] text-xs font-semibold">{pendingCount} listing{pendingCount !== 1 ? 's' : ''} under review</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/host-dashboard')} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-90" style={{ background: 'rgba(255,215,0,0.1)', color: '#ffd700', border: '1px solid rgba(255,215,0,0.2)' }}>
              <Icon name="LayoutDashboard" size={14} />
              Dashboard
            </button>
            <button onClick={() => navigate('/list-my-car')} className="btn-primary text-sm py-2 px-4">
              <Icon name="Plus" size={16} /> Add Vehicle
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { label: 'Total Vehicles', value: vehicles?.length, icon: 'Car', color: '#ffd700' },
            { label: 'Active Listings', value: vehicles?.filter(v => v?.status === 'active')?.length, icon: 'CheckCircle', color: '#34d399' },
            { label: 'Total Bookings', value: bookings?.length, icon: 'Calendar', color: '#60a5fa' },
          ]?.map(stat => (
            <div key={stat?.label} className="rounded-2xl p-4 text-center" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <Icon name={stat?.icon} size={20} className="mx-auto mb-2" style={{ color: stat?.color }} />
              <div className="text-white font-black text-2xl">{stat?.value}</div>
              <div className="text-white/40 text-xs">{stat?.label}</div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2]?.map(i => <div key={i} className="h-40 rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.05)' }} />)}
          </div>
        ) : vehicles?.length === 0 ? (
          <div className="text-center py-24">
            <div className="text-6xl mb-4">🚗</div>
            <h3 className="text-white font-bold text-xl mb-2">No vehicles listed yet</h3>
            <p className="text-white/40 text-sm mb-6">Start earning by listing your vehicle</p>
            <button onClick={() => navigate('/list-my-car')} className="btn-primary">List Your Car</button>
          </div>
        ) : (
          <div className="space-y-4">
            {vehicles?.map(vehicle => {
              const status = statusConfig?.[vehicle?.status] || statusConfig?.draft;
              const pricing = vehicle?.vehicle_pricing?.[0];
              const stats = getVehicleStats(vehicle?.id);
              const mediaCount = vehicle?.vehicle_media?.length || 0;
              const hasPhotoIssue = mediaCount < 6;
              const isPending = vehicle?.status === 'pending_review';
              const isDraft = vehicle?.status === 'draft';

              return (
                <div key={vehicle?.id} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="flex items-start gap-4">
                    <VehicleThumbnail media={vehicle?.vehicle_media || []} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="text-white font-bold text-base truncate">{vehicle?.title}</h3>
                        {/* Status badge */}
                        <span className="flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: status?.bg, color: status?.color }}>
                          <Icon name={status?.icon} size={11} />
                          {status?.label}
                        </span>
                      </div>
                      <p className="text-white/40 text-xs mb-2">{vehicle?.location_address} · {vehicle?.rental_mode?.replace('_', ' ')}</p>

                      {/* Status description for pending/draft */}
                      {(isPending || isDraft) && (
                        <p className="text-xs mb-2" style={{ color: status?.color + 'cc' }}>{status?.desc}</p>
                      )}

                      {/* Submission status details for pending */}
                      {isPending && (
                        <div className="rounded-xl p-3 mb-3 space-y-2" style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.15)' }}>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-white/50 flex items-center gap-1.5">
                              <Icon name="Camera" size={12} />
                              Photos uploaded
                            </span>
                            <span className={`font-bold ${mediaCount >= 6 ? 'text-[#34d399]' : 'text-[#f87171]'}`}>
                              {mediaCount} / 6 min
                              {mediaCount < 6 && ' ⚠️'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-white/50 flex items-center gap-1.5">
                              <Icon name="DollarSign" size={12} />
                              Pricing set
                            </span>
                            <span className={`font-bold ${pricing ? 'text-[#34d399]' : 'text-[#f87171]'}`}>
                              {pricing ? `KES ${pricing?.base_daily_rate?.toLocaleString()}/day` : 'Missing ⚠️'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-white/50 flex items-center gap-1.5">
                              <Icon name="Clock" size={12} />
                              Submitted
                            </span>
                            <span className="text-white/60 font-semibold">
                              {vehicle?.created_at ? new Date(vehicle?.created_at)?.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                            </span>
                          </div>
                          <CompletenessBar vehicle={vehicle} mediaCount={mediaCount} pricing={pricing} />
                        </div>
                      )}

                      {/* Earnings stats for active vehicles */}
                      {vehicle?.status === 'active' && (
                        <div className="flex flex-wrap gap-4 text-sm">
                          <div><span className="text-white/40 text-xs">Daily Rate</span><div className="text-[#ffd700] font-bold">KES {pricing?.base_daily_rate?.toLocaleString() || '—'}</div></div>
                          <div><span className="text-white/40 text-xs">Bookings</span><div className="text-white font-bold">{stats?.totalBookings}</div></div>
                          <div><span className="text-white/40 text-xs">Earnings</span><div className="text-[#34d399] font-bold">KES {stats?.earnings?.toLocaleString()}</div></div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="mt-4 pt-4 border-t border-white/8 flex flex-wrap gap-2">
                    {vehicle?.status === 'active' && (
                      <button onClick={() => navigate(`/booking-requests?vehicleId=${vehicle?.id}`)} className="btn-secondary text-xs py-2 px-3">
                        <Icon name="Calendar" size={14} /> Booking Requests
                      </button>
                    )}

                    {/* Edit button for pending or draft */}
                    {(isPending || isDraft) && (
                      <button
                        onClick={() => navigate(`/list-my-car?edit=${vehicle?.id}`)}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-90"
                        style={{ background: 'rgba(96,165,250,0.12)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.25)' }}
                      >
                        <Icon name="Edit2" size={13} />
                        Edit Details
                      </button>
                    )}

                    {/* Retry upload button — shown when photo count is below minimum */}
                    {hasPhotoIssue && (
                      <button
                        onClick={() => setRetryVehicle(vehicle)}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-90"
                        style={{ background: 'rgba(248,113,113,0.12)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}
                      >
                        <Icon name="RefreshCw" size={13} />
                        Retry Upload ({mediaCount}/6 photos)
                      </button>
                    )}

                    {/* Pending review notice */}
                    {isPending && !hasPhotoIssue && (
                      <span className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold" style={{ background: 'rgba(251,191,36,0.1)', color: '#fbbf24' }}>
                        <Icon name="Clock" size={13} />
                        Awaiting admin review
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyVehiclesScreen;
